// src/pages/help/SupportTicketDetailModal.tsx

import React, { useState, useEffect, useRef } from "react";
import { Modal, Typography, Tag, Divider, Space, Button, Input, Upload, Avatar } from "antd";
import { SendOutlined, PaperClipOutlined, UserOutlined } from "@ant-design/icons";
import { getSessionUser } from "../../utils/auth/sessionUser";
import dayjs from "dayjs";
import type { UploadFile } from "antd/es/upload/interface";
import { getSupportTicket, addMessage, formatFileSize } from "../../api/supportTickets/supportTicketsApi";
import { SupportTicket, SupportTicketMessage } from "../../types/communications/supportTickets";
import { useNotifications } from "../../context/NotificationContext";
import { InlineLoading } from "../../components/shared";

const { Text, Title } = Typography;
const { TextArea } = Input;

interface SupportTicketDetailModalProps {
	open: boolean;
	ticket: SupportTicket;
	onClose: () => void;
	isAdmin: boolean;
}

const SupportTicketDetailModal: React.FC<SupportTicketDetailModalProps> = ({ open, ticket: initialTicket, onClose, isAdmin }) => {
	const { showSuccess, showError } = useNotifications();
	const [ticket, setTicket] = useState<SupportTicket>(initialTicket);
	const [loading, setLoading] = useState(false);
	const [messageText, setMessageText] = useState("");
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [sending, setSending] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	const user = getSessionUser();

	useEffect(() => {
		if (open) {
			loadTicketDetails();
		}
	}, [open, ticket.id]);

	useEffect(() => {
		scrollToBottom();
	}, [ticket.messages]);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const loadTicketDetails = async () => {
		setLoading(true);
		try {
			const data = await getSupportTicket(ticket.id, user.id);
			setTicket(data);
		} catch (error) {
			console.error("Error loading ticket details:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleSendMessage = async () => {
		if (!messageText.trim() && fileList.length === 0) return;

		setSending(true);
		try {
			const files = fileList.map((f) => f.originFileObj as File);

			await addMessage(ticket.id, {
				sender_user_id: user.id,
				message: messageText.trim() || " ",
				attachments: files.length > 0 ? files : undefined,
			});

			setMessageText("");
			setFileList([]);
			await loadTicketDetails();
			showSuccess("Message sent successfully");
		} catch (error) {
			showError("Failed to send message");
		} finally {
			setSending(false);
		}
	};

	const renderMessage = (msg: SupportTicketMessage) => {
		const isOwnMessage = msg.sender_clinical_user_id === user.id;
		const senderName = msg.sender_admin_id
			? (msg.sender_admin?.name ?? 'Support Team')
			: (msg.sender ? `${msg.sender.first_name} ${msg.sender.last_name}` : 'Unknown');
		const sender = msg.sender;

		return (
			<div key={msg.id} style={{ marginBottom: 16, display: "flex", justifyContent: isOwnMessage ? "flex-end" : "flex-start" }}>
				<div style={{ maxWidth: "70%", display: "flex", flexDirection: "column", alignItems: isOwnMessage ? "flex-end" : "flex-start" }}>
					{!isOwnMessage && (
						<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
							<Avatar size="small" icon={<UserOutlined />} src={sender?.profile_picture} />
							<Text type="secondary" style={{ fontSize: 11 }}>{senderName}</Text>
						</div>
					)}

					<div style={{ padding: 12, borderRadius: 8, backgroundColor: isOwnMessage ? "var(--mg-primary)" : "var(--mg-bg-subtle)", color: isOwnMessage ? "white" : "inherit" }}>
						<Text style={{ color: isOwnMessage ? "white" : "var(--mg-text)" }}>{msg.message}</Text>

						{msg.attachments && msg.attachments.length > 0 && (
							<div style={{ marginTop: 8 }}>
								{msg.attachments.map((att) => (
									<div key={att.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginTop: 4 }}>
										<a href={att.file_url} target="_blank" rel="noopener noreferrer" style={{ color: isOwnMessage ? "white" : "var(--mg-primary)", textDecoration: "underline" }}>
											{att.file_name} ({formatFileSize(att.file_size)})
										</a>
									</div>
								))}
							</div>
						)}
					</div>

					<Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>{dayjs(msg.created_at).format("DD/MM/YYYY HH:mm")}</Text>
				</div>
			</div>
		);
	};

	return (
		<Modal
			title={
				<Space direction="vertical" size={0}>
					<Title level={5} style={{ margin: 0 }}>
						{ticket.subject}
					</Title>
					<Space size={8}>
						<Tag style={{ 
							backgroundColor: ticket.status === "resolved" || ticket.status === "closed" ? "var(--mg-success)" : (ticket.status === "in_progress" ? "var(--mg-info)" : "var(--mg-primary)"),
							color: "white",
							border: "none"
						}}>{ticket.status.replace("_", " ").toUpperCase()}</Tag>
						<Tag style={{ 
							backgroundColor: ticket.priority === "urgent" ? "var(--mg-danger)" : (ticket.priority === "high" ? "var(--mg-warning)" : "var(--mg-primary)"),
							color: "white",
							border: "none"
						}}>{ticket.priority.toUpperCase()}</Tag>
					</Space>
				</Space>
			}
			open={open}
			onCancel={onClose}
			footer={null}
			width={800}
			style={{ maxHeight: "90vh" }}
		>
			<div style={{ display: "flex", flexDirection: "column", height: 600 }}>
				<div>
					<Text strong>Description:</Text>
					<div style={{ marginTop: 8 }}>{ticket.description}</div>

					{ticket.attachments && ticket.attachments.length > 0 && (
						<div style={{ marginTop: 12 }}>
							<Text strong>Initial Attachments:</Text>
							<div style={{ marginTop: 8 }}>
								{ticket.attachments.map((att) => (
									<div key={att.id} style={{ fontSize: 13 }}>
										<a href={att.file_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--mg-primary)" }}>
											{att.file_name} ({formatFileSize(att.file_size)})
										</a>
									</div>
								))}
							</div>
						</div>
					)}
				</div>

				<Divider />

<div style={{ flex: 1, overflowY: "auto", marginBottom: 16, paddingLeft: 8, paddingRight: 8 }}>
				{loading ? (
					<InlineLoading minHeight={200} message="Loading messages…" />
					) : (
						<>
							{ticket.messages?.map(renderMessage)}
							<div ref={messagesEndRef} />
						</>
					)}
				</div>

				{!ticket.status.includes("closed") && !ticket.status.includes("resolved") && (
					<>
						{fileList.length > 0 && (
							<div style={{ marginBottom: 8 }}>
								{fileList.map((file, index) => (
									<Tag key={index} closable onClose={() => setFileList(fileList.filter((_, i) => i !== index))}>
										{file.name}
									</Tag>
								))}
							</div>
						)}

						<Space.Compact style={{ width: "100%" }}>
							<Upload fileList={[]} onChange={({ fileList }) => setFileList([...fileList])} beforeUpload={() => false} maxCount={10} showUploadList={false}>
								<Button icon={<PaperClipOutlined />} disabled={sending || fileList.length >= 10} />
							</Upload>

							<TextArea
								value={messageText}
								onChange={(e) => setMessageText(e.target.value)}
								placeholder="Type your message..."
								autoSize={{ minRows: 1, maxRows: 4 }}
								disabled={sending}
								onPressEnter={(e) => {
									if (!e.shiftKey) {
										e.preventDefault();
										handleSendMessage();
									}
								}}
							/>

							<Button type="primary" icon={<SendOutlined />} onClick={handleSendMessage} loading={sending} disabled={!messageText.trim() && fileList.length === 0}>
								Send
							</Button>
						</Space.Compact>
					</>
				)}
			</div>
		</Modal>
	);
};

export default SupportTicketDetailModal;
